import _ from "lodash";

import {
	findDisplayRulesByValues,
	getListOfValueFromDisplayRules,
} from "containers/Form/SimplifiedBuilder/Internal/DisplayRulesUtil";
import { getFormElementsFromFormConfig } from "containers/Form/SimplifiedBuilder/Internal/FormConverter";
import { CAYUSE_TYPES, FIELD_TYPES } from "globals/constants";

export const createRulesExpression = groupsWithLetters => {
	let rulesExpression = "";
	for (let j = 0; j < groupsWithLetters.length; j++) {
		for (let i = 0; i < groupsWithLetters[j].length; i++) {
			if (i === 0) {
				rulesExpression = rulesExpression + "(";
			} else if (i !== groupsWithLetters[j].length) {
				rulesExpression = rulesExpression + " + ";
			}
			rulesExpression = rulesExpression + groupsWithLetters[j][i].letter;

			if (i === groupsWithLetters[j].length - 1) {
				rulesExpression = rulesExpression + ")";
			}
		}

		if (groupsWithLetters.length > 1 && j !== groupsWithLetters.length - 1 && groupsWithLetters[j + 1].length > 0) {
			rulesExpression = rulesExpression + " OR ";
		}
	}

	return rulesExpression;
};

export const assignLettersToRuleGroups = groupList => {
	let letter = 65;
	let totalRulesWithLetter = [];
	for (const group of groupList) {
		for (const rule of group) {
			if (rule.id !== undefined) {
				totalRulesWithLetter.push({
					letter: String.fromCharCode(letter),
					rule: rule
				});
			} else {
				rule.letter = String.fromCharCode(letter);
				totalRulesWithLetter.push(rule);
			}
			letter++;
		}
	}

	return groupList.map(group => totalRulesWithLetter.splice(0, group.length));
};

export const getInitialRules = (parentQuestionConfig, completeFormModel, ruleType) => {
	const field = parentQuestionConfig[ruleType];
	if (typeof field === "string" && field !== "{{}}") {
		const rules = getListOfValueFromDisplayRules(field.replace(" ? false : true", ""));
		const DP = getFormElementsFromFormConfig(completeFormModel);
		const foundRules = findDisplayRulesByValues(rules, DP);

		return assignLettersToRuleGroups(foundRules);
	}

	return [];
};

const removeExtraBrackets = val => val.replace(/{{/, "").replace(/}}/, "");
const extractItemText = rule => rule.itemText || rule.items[rule.itemToSelect].text;
const extractItemId = rule => rule.itemId;

export const createExpressionAndAddToParentQuestion = (groupList, ruleType) =>
	(ruleType === "visible" || ruleType === "enable")
		? createVisibleExpressionAndAddToParentQuestion(groupList)
		: createIgnoreExpressionAndAddToParentQuestion(groupList);

const TEXT_INPUTS = [ FIELD_TYPES.TEXT, FIELD_TYPES.RICH_TEXT, FIELD_TYPES.TEXTAREA ];
const NUMBER_INPUTS = [ FIELD_TYPES.ONE, FIELD_TYPES.NUMBER, FIELD_TYPES.DATE ];
const SELECT_INPUTS = [ FIELD_TYPES.SELECT, FIELD_TYPES.RADIO, FIELD_TYPES.FUNDING_PROGRAM ];
const CHECKBOX_INPUT = [ FIELD_TYPES.CAYUSE_CHECKBOXES ];
const CAYUSE_TYPE_INPUTS = [ CAYUSE_TYPES.ORG_FINDER, CAYUSE_TYPES.UNIT_FINDER, CAYUSE_TYPES.PERSON_FINDER ];

const createExpressionForRule = rule => {
	let expression = removeExtraBrackets(rule.value);
	const ruleType = rule.type;

	if (TEXT_INPUTS.includes(ruleType)) {
		expression = `${expression}.length > 0`;
	} else if (NUMBER_INPUTS.includes(ruleType)) {
		expression = `${expression}.toString().length > 0`;
	} else if (_.has(rule, "itemToSelect") && SELECT_INPUTS.includes(ruleType)) {
		const itemText = extractItemText(rule);
		expression = `${expression} == '${itemText}'`;
	} else if (_.has(rule, "itemToSelect") && CHECKBOX_INPUT.includes(ruleType)) {
		const itemText = extractItemText(rule);
		expression = `${expression}['${itemText}']`;
	} else if (CAYUSE_TYPE_INPUTS.includes(rule.cayuseType) && _.has(rule, "itemId")) {
		const itemId = extractItemId(rule);
		expression = `${expression}.includes('${itemId}')`;
	} else if (CAYUSE_TYPE_INPUTS.includes(rule.cayuseType)) {
		expression = `${expression}.length > 0`;
	} else if (_.has(rule, "itemId") && SELECT_INPUTS.includes(ruleType)) {
		const itemId = extractItemId(rule);
		expression = `flw.mapAttr(${expression}, 'id').includes('${itemId}')`;
	} else if (rule.cayuseType === CAYUSE_TYPES.FUNDING_PROGRAM) {
		expression = `flw.remove.nulls(flw.mapAttr(${expression}, 'id')).length > 0`;
	} else if (rule.cayuseType === CAYUSE_TYPES.CLASSIFICATION_CODES && _.has(rule, "itemId")) {
		const itemId = extractItemId(rule);
		expression = `flw.mapAttr(${expression}, 'code').includes('${itemId}')`;
	} else if (rule.cayuseType === CAYUSE_TYPES.CLASSIFICATION_CODES) {
		expression = `flw.remove.nulls(flw.mapAttr(${expression}, 'code')).length > 0`;
	}

	return expression;
};

const createVisibleExpressionAndAddToParentQuestion = groupList => {
	let visible = "{{ ";
	for (let j = 0; j < groupList.length; j++) {
		let displayRuleList = groupList[j];
		for (let i = 0; i < displayRuleList.length; i++) {
			const rule = displayRuleList[i].rule;
			visible = `${visible} ${createExpressionForRule(rule)}`;
			if (i < displayRuleList.length - 1) {
				visible = `${visible} && `;
			}
		}
		if (j < groupList.length - 1 && groupList[j + 1].length > 0) {
			visible = `${visible} || `;
		}
	}

	return visible === "{{ "
		? true
		: `${visible} }}`;
};

const createIgnoreExpressionAndAddToParentQuestion = groupList => {
	let expression = "";

	for (let j = 0; j < groupList.length; j++) {
		const displayRuleList = groupList[j];
		for (let i = 0; i < displayRuleList.length; i++) {
			const { rule } = displayRuleList[i];
			expression = `${expression} ${createExpressionForRule(rule)}`;
			if (i < displayRuleList.length - 1) {
				expression = expression + " && ";
			}
		}
		if (j < groupList.length - 1 && groupList[j + 1].length > 0) {
			expression = expression + " || ";
		}
	}

	if (expression === "") {
		// there is no expression

		return false;
	} else {
		expression = `{{ ${expression} ? false : true }}`;

		return expression;
	}
};

const SEARCHABLE_TYPES = [
	CAYUSE_TYPES.ORG_FINDER,
	CAYUSE_TYPES.UNIT_FINDER,
	CAYUSE_TYPES.FUNDING_PROGRAM,
	CAYUSE_TYPES.PERSON_FINDER,
	CAYUSE_TYPES.CLASSIFICATION_CODES,
];

export const isSearchableType = cayuseType => {
	return SEARCHABLE_TYPES.includes(cayuseType);
};
